import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Save } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useGlobalKeyboardShortcut } from '@/hooks/useGlobalKeyboardShortcut'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import {
  useCreateNoteSession,
  useUpdateNoteSession,
  useNoteSession,
  useDeleteNoteSession,
} from '@/hooks/use-api'
import { NoteEditor, type NoteEditorHandle } from './NoteEditor'
import { TemplateSelector } from './TemplateSelector'
import { SessionTimer } from './SessionTimer'
import { CompleteSessionButton } from './CompleteSessionButton'
import type { NoteTemplate } from '@/types/noteSession'
import { socketClient } from '@/lib/socket'
import { useQueryClient } from '@tanstack/react-query'
import { explainError } from '@/lib/explain-error'

interface NoteSessionPanelProps {
  entityType: 'Lead' | 'Contact' | 'Opportunity' | 'Account'
  entityId: string
  entityName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  readOnly?: boolean
  existingSessionId?: string
  allowEdit?: boolean
}

export function NoteSessionPanel({
  entityType,
  entityId,
  entityName,
  open,
  onOpenChange,
  readOnly = false,
  existingSessionId,
  allowEdit = false,
}: NoteSessionPanelProps) {
  const [sessionId, setSessionId] = useState<string>()
  const [title, setTitle] = useState('')
  // Track title and content dirtiness separately so a saved title doesn't mask pending
  // content edits (and vice versa). isDirty drives the unsaved-changes prompts.
  const [titleDirty, setTitleDirty] = useState(false)
  const [contentDirty, setContentDirty] = useState(false)
  const isDirty = titleDirty || contentDirty
  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  const triggerRef = useRef<HTMLElement | null>(null)
  const editorRef = useRef<NoteEditorHandle>(null)
  const complementaryRef = useRef<HTMLElement | null>(null)

  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createSessionMutation = useCreateNoteSession()
  const updateSessionMutation = useUpdateNoteSession()
  const deleteSessionMutation = useDeleteNoteSession()
  const { data: session, isLoading } = useNoteSession(sessionId ?? '')

  // Refs that survive the close transition so cleanup can read the prior values
  // (parent clears existingSessionId on close; reset effect clears sessionId).
  const wasNewSessionRef = useRef(false)
  // Guards so the close cleanup never deletes a note the user actually kept:
  // set when the session is completed, or when the editor ever held content.
  // These are authoritative (not the possibly-stale `session` snapshot).
  const completedRef = useRef(false)
  const hadContentRef = useRef(false)
  const sessionRef = useRef(session)
  const sessionIdRef = useRef(sessionId)
  useEffect(() => {
    sessionRef.current = session
  }, [session])
  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])
  // Capture whether THIS open cycle was a fresh-create or a load-existing, and
  // reset the keep-guards for the new cycle.
  useEffect(() => {
    if (open) {
      wasNewSessionRef.current = !existingSessionId
      completedRef.current = false
      hadContentRef.current = false
    }
  }, [open, existingSessionId])

  // Auto-cleanup: when the panel closes, soft-delete the session ONLY if WE created
  // it this cycle AND the user neither completed it nor ever entered content.
  // Guarded by refs (not the possibly-stale `session` snapshot) so a just-completed
  // or just-typed note is never deleted by the close race.
  useEffect(() => {
    if (open) return
    const sid = sessionIdRef.current
    if (!wasNewSessionRef.current) return
    if (!sid) return
    if (completedRef.current) return
    if (hadContentRef.current) return
    deleteSessionMutation.mutate(sid)
  }, [open, deleteSessionMutation])

  // Listen for real-time updates
  useEffect(() => {
    const handleAutosaved = (data: { id: string; last_modified_date: string }) => {
      if (data.id === sessionId) {
        queryClient.invalidateQueries({ queryKey: ['note-sessions', sessionId] })
        // Invalidate entity-scoped queries
        if (entityType === 'Contact') {
          queryClient.invalidateQueries({ queryKey: ['note-sessions', 'by-contact', entityId] })
        } else if (entityType === 'Lead') {
          queryClient.invalidateQueries({ queryKey: ['note-sessions', 'by-lead', entityId] })
        } else if (entityType === 'Opportunity') {
          queryClient.invalidateQueries({ queryKey: ['note-sessions', 'by-opportunity', entityId] })
        } else if (entityType === 'Account') {
          queryClient.invalidateQueries({ queryKey: ['note-sessions', 'by-account', entityId] })
        }
      }
    }

    const handleCompleted = () => {
      completedRef.current = true
      queryClient.invalidateQueries({ queryKey: ['note-sessions'] })
      // Invalidate entity-scoped queries
      if (entityType === 'Contact') {
        queryClient.invalidateQueries({ queryKey: ['note-sessions', 'by-contact', entityId] })
      } else if (entityType === 'Lead') {
        queryClient.invalidateQueries({ queryKey: ['note-sessions', 'by-lead', entityId] })
      } else if (entityType === 'Opportunity') {
        queryClient.invalidateQueries({ queryKey: ['note-sessions', 'by-opportunity', entityId] })
      } else if (entityType === 'Account') {
        queryClient.invalidateQueries({ queryKey: ['note-sessions', 'by-account', entityId] })
      }
      onOpenChange(false)
      toast({
        title: 'Session completed',
        description: 'Your notes are saved and visible in the Notes tab.',
      })
    }

    socketClient.onNoteSessionAutosaved(handleAutosaved)
    socketClient.onNoteSessionCompleted(handleCompleted)

    return () => {
      socketClient.offNoteSessionAutosaved(handleAutosaved)
      socketClient.offNoteSessionCompleted(handleCompleted)
    }
  }, [sessionId, entityType, entityId, queryClient, onOpenChange, toast])

  // Load existing session or create new session when panel opens.
  // isCreatingRef guards against double-create: TanStack Query's mutation object
  // is a new reference every render, so this effect re-runs on parent re-renders.
  // Without the ref, a re-render before the in-flight POST resolves would fire
  // a second mutate() with sessionId still undefined.
  const isCreatingRef = useRef(false)
  useEffect(() => {
    if (!open || sessionId || readOnly) return

    // If existingSessionId provided, load that session instead of creating
    if (existingSessionId) {
      setSessionId(existingSessionId)
      return
    }

    if (isCreatingRef.current) return
    isCreatingRef.current = true

    const defaultTitle = `Call with ${entityName || entityType}`

    // Opportunity & Account attach via what_id/what_type; Lead & Contact via who_id/who_type.
    const isWhatEntity = entityType === 'Opportunity' || entityType === 'Account'
    createSessionMutation.mutate(
      {
        title: defaultTitle,
        who_id: isWhatEntity ? undefined : entityId,
        who_type: isWhatEntity ? undefined : entityType,
        what_id: isWhatEntity ? entityId : undefined,
        what_type: isWhatEntity ? entityType : undefined,
      },
      {
        onSuccess: (newSession) => {
          setSessionId(newSession.id)
          setTitle(newSession.title ?? '')
          isCreatingRef.current = false
          toast({
            title: 'Session started',
            description: 'Your notes will auto-save as you type.',
          })
        },
        onError: (error) => {
          isCreatingRef.current = false
          toast({
            title: 'Failed to start session',
            description: explainError(error, 'An error occurred'),
            variant: 'destructive',
          })
          onOpenChange(false)
        },
      }
    )
  }, [
    open,
    sessionId,
    readOnly,
    existingSessionId,
    entityType,
    entityId,
    entityName,
    createSessionMutation,
    onOpenChange,
    toast,
  ])

  // Reset the create-guard when the panel closes so reopening can create again
  useEffect(() => {
    if (!open) isCreatingRef.current = false
  }, [open])

  // Handle template selection
  const handleTemplateSelect = useCallback(
    (template: NoteTemplate | null) => {
      if (!sessionId || !template) return

      // Prepend template content to notes
      updateSessionMutation.mutate(
        {
          id: sessionId,
          data: {
            template_used: template.name,
            notes_content: template.content,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: 'Template applied',
              description: `${template.name} template has been loaded.`,
            })
            queryClient.invalidateQueries({ queryKey: ['note-sessions', sessionId] })
          },
        }
      )
    },
    [sessionId, updateSessionMutation, toast, queryClient]
  )

  // Handle title update
  const handleTitleSave = () => {
    if (!sessionId || !title.trim()) return

    updateSessionMutation.mutate(
      {
        id: sessionId,
        data: { title: title.trim() },
      },
      {
        onSuccess: () => {
          setTitleDirty(false)
          toast({ title: 'Title updated' })
        },
      }
    )
  }

  // Handle close: flush any pending edit first so closing saves rather than discards.
  // Only fall back to the discard confirmation if the flush genuinely fails.
  const handleClose = async () => {
    try {
      await editorRef.current?.flush()
      onOpenChange(false)
    } catch {
      triggerRef.current = document.activeElement as HTMLElement
      setShowCloseConfirm(true)
    }
  }

  // Handle confirmed close
  const handleConfirmClose = () => {
    setShowCloseConfirm(false)
    onOpenChange(false)
  }

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to close
      if (e.key === 'Escape') {
        handleClose()
      }

      // Ctrl/Cmd + Enter to complete
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        // Complete handled by CompleteSessionButton
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, isDirty, handleClose])

  // Reset state when panel closes
  useEffect(() => {
    if (!open) {
      setSessionId(undefined)
      setTitle('')
      setTitleDirty(false)
      setContentDirty(false)
      setShowCloseConfirm(false)

      // Restore focus to trigger element
      if (triggerRef.current) {
        triggerRef.current.focus()
        triggerRef.current = null
      }
    } else {
      // Focus the complementary region when panel opens
      if (complementaryRef.current) {
        complementaryRef.current.focus()
      }
    }
  }, [open])

  // Global keyboard shortcut: Ctrl/Cmd + Shift + N
  useGlobalKeyboardShortcut(
    {
      key: 'n',
      ctrlOrCmd: true,
      shift: true,
      preventDefault: true,
      excludeInputs: false, // Allow even in inputs when modifier is pressed
    },
    () => {
      if (!readOnly) {
        onOpenChange(!open)
      }
    }
  )

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose()
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
          {/* Wrap content in aside with complementary role */}
          <aside
            ref={complementaryRef}
            role="complementary"
            aria-label={`Call notes for ${entityName || entityType}`}
            tabIndex={-1}
            className="flex flex-col h-full p-6"
          >
            {readOnly && (
              <div className="mb-4 p-3 bg-muted rounded-md border border-border">
                <p className="text-sm text-muted-foreground">
                  Read-only while impersonating. You cannot create or modify notes.
                </p>
              </div>
            )}

            <SheetHeader>
              <SheetTitle>Call Notes</SheetTitle>
              <SheetDescription>{entityName && `Notes for ${entityName}`}</SheetDescription>
            </SheetHeader>

            {isLoading && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div
                    className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    role="progressbar"
                    aria-label="Loading session"
                  />
                  <p className="mt-2 text-sm text-muted-foreground">Starting session...</p>
                </div>
              </div>
            )}

            {session && (
              <div className="flex-1 flex flex-col gap-4 overflow-hidden">
                {/* Session metadata */}
                <div className="flex items-center justify-between gap-4">
                  <SessionTimer startedAt={session.started_at} />
                  <div className="text-xs text-muted-foreground">
                    Session type: {session.session_type}
                  </div>
                </div>

                {/* Title editor */}
                <div className="space-y-2">
                  <Label htmlFor="session-title">Title</Label>
                  <div className="flex gap-2">
                    <Input
                      id="session-title"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value)
                        setTitleDirty(true)
                      }}
                      onBlur={handleTitleSave}
                      placeholder="Session title"
                      aria-label="Session title"
                      disabled={readOnly || (session.status !== 'In Progress' && !allowEdit)}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handleTitleSave}
                      aria-label="Save title"
                      disabled={readOnly || !title.trim() || title === session.title}
                    >
                      <Save className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>

                {/* Template selector */}
                <TemplateSelector
                  value={session.template_used}
                  onSelect={handleTemplateSelect}
                  disabled={readOnly || !!session.notes_content}
                />

                {/* Note editor */}
                <NoteEditor
                  ref={editorRef}
                  sessionId={session.id}
                  initialContent={session.notes_content}
                  onDirtyChange={setContentDirty}
                  onContentChange={(c) => {
                    if ((c ?? '').trim().length > 0) hadContentRef.current = true
                  }}
                  disabled={readOnly || (session.status !== 'In Progress' && !allowEdit)}
                />

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={handleClose} aria-label="Close panel">
                    <X className="mr-2 h-4 w-4" aria-hidden="true" />
                    Close
                  </Button>
                  <CompleteSessionButton
                    sessionId={session.id}
                    hasUnsavedChanges={isDirty}
                    getContent={() => editorRef.current?.getContent()}
                    onComplete={() => {
                      completedRef.current = true
                      onOpenChange(false)
                    }}
                    disabled={readOnly || (session.status !== 'In Progress' && !allowEdit)}
                  />
                </div>
              </div>
            )}
          </aside>
        </SheetContent>
      </Sheet>

      {/* Close confirmation dialog */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close this panel?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
